/**
 * MarketSelectionContext
 * PRD-041: Market Dashboard Redesign - Phase 1 & 2
 *
 * Provides shared state for commodity selection across the market dashboard.
 * Allows users to select multiple commodities for comparison in charts.
 * Phase 2: Includes Supabase Realtime subscriptions for live updates.
 */

import React, { createContext, useContext, useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { supabase } from '../services/supabaseClient';
import type { RealtimeChannel } from '@supabase/supabase-js';

// Types
export interface SelectedCommodity {
  id: string;
  name: string;
  slug?: string;
  category?: string;
  avgPrice?: number;
  priceChange?: number;
}

export type ChartType = 'price' | 'volume' | 'volatility';
export type TimeWindow = '24h' | '7d' | '30d' | '90d';

interface MarketSelectionState {
  // Selected commodities for comparison
  selectedCommodities: SelectedCommodity[];

  // Chart configuration
  activeChart: ChartType;
  timeWindow: TimeWindow;

  // Filters
  systemFilter: string | null;
  categoryFilter: string | null;

  // UI state
  isComparisonPanelOpen: boolean;
  maxSelections: number;
}

// Realtime update event types
export interface PriceUpdateEvent {
  commodityId: string;
  newPrice: number;
  activity: 'buy' | 'sell';
  source: string;
  timestamp: string;
}

export interface MarketUpdateListener {
  onPriceUpdate?: (event: PriceUpdateEvent) => void;
  onNewCommodity?: (commodityId: string) => void;
  onNewLocation?: (locationId: string) => void;
}

interface MarketSelectionContextType extends MarketSelectionState {
  // Selection methods
  addCommodity: (commodity: SelectedCommodity) => void;
  removeCommodity: (id: string) => void;
  toggleCommodity: (commodity: SelectedCommodity) => void;
  clearSelection: () => void;
  isSelected: (id: string) => boolean;

  // Chart configuration methods
  setActiveChart: (chart: ChartType) => void;
  setTimeWindow: (window: TimeWindow) => void;

  // Filter methods
  setSystemFilter: (system: string | null) => void;
  setCategoryFilter: (category: string | null) => void;

  // UI methods
  toggleComparisonPanel: () => void;
  setComparisonPanelOpen: (open: boolean) => void;

  // Computed values
  selectionCount: number;
  canAddMore: boolean;
  hasSelection: boolean;

  // Phase 2: Realtime updates
  lastPriceUpdate: PriceUpdateEvent | null;
  isRealtimeConnected: boolean;
  registerUpdateListener: (listener: MarketUpdateListener) => () => void;
  forceRefresh: () => void;
}

const MarketSelectionContext = createContext<MarketSelectionContextType | null>(null);

// Default values
const DEFAULT_MAX_SELECTIONS = 5;
const DEFAULT_TIME_WINDOW: TimeWindow = '7d';
const DEFAULT_CHART_TYPE: ChartType = 'price';

// Local storage key for persisting selections
const STORAGE_KEY = 'orbital-ledger-market-selection';

interface StoredState {
  selectedCommodityIds: string[];
  activeChart: ChartType;
  timeWindow: TimeWindow;
  systemFilter: string | null;
  categoryFilter: string | null;
}

export const MarketSelectionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Core state
  const [selectedCommodities, setSelectedCommodities] = useState<SelectedCommodity[]>([]);
  const [activeChart, setActiveChartState] = useState<ChartType>(DEFAULT_CHART_TYPE);
  const [timeWindow, setTimeWindowState] = useState<TimeWindow>(DEFAULT_TIME_WINDOW);
  const [systemFilter, setSystemFilterState] = useState<string | null>(null);
  const [categoryFilter, setCategoryFilterState] = useState<string | null>(null);
  const [isComparisonPanelOpen, setIsComparisonPanelOpen] = useState<boolean>(false);
  const [isInitialized, setIsInitialized] = useState<boolean>(false);

  // Phase 2: Realtime state
  const [lastPriceUpdate, setLastPriceUpdate] = useState<PriceUpdateEvent | null>(null);
  const [isRealtimeConnected, setIsRealtimeConnected] = useState<boolean>(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_refreshTrigger, setRefreshTrigger] = useState<number>(0);
  const channelRef = useRef<RealtimeChannel | null>(null);
  const listenersRef = useRef<Set<MarketUpdateListener>>(new Set());

  // Load persisted state on mount
  useEffect(() => {
    const loadPersistedState = async () => {
      try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) {
          const parsed: StoredState = JSON.parse(stored);

          // Restore chart settings
          if (parsed.activeChart) setActiveChartState(parsed.activeChart);
          if (parsed.timeWindow) setTimeWindowState(parsed.timeWindow);
          if (parsed.systemFilter) setSystemFilterState(parsed.systemFilter);
          if (parsed.categoryFilter) setCategoryFilterState(parsed.categoryFilter);

          // Restore selected commodities by fetching fresh data
          if (parsed.selectedCommodityIds && parsed.selectedCommodityIds.length > 0) {
            const { data: commodities } = await supabase
              .from('commodities_new')
              .select('id, name, slug, category')
              .in('id', parsed.selectedCommodityIds);

            if (commodities && commodities.length > 0) {
              setSelectedCommodities(commodities.map(c => ({
                id: c.id,
                name: c.name,
                slug: c.slug,
                category: c.category
              })));
              setIsComparisonPanelOpen(true);
            }
          }
        }
      } catch (error) {
        console.error('[MarketSelectionContext] Error loading persisted state:', error);
      } finally {
        setIsInitialized(true);
      }
    };

    loadPersistedState();
  }, []);

  // Persist state changes
  useEffect(() => {
    if (!isInitialized) return;

    try {
      const stateToStore: StoredState = {
        selectedCommodityIds: selectedCommodities.map(c => c.id),
        activeChart,
        timeWindow,
        systemFilter,
        categoryFilter
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(stateToStore));
    } catch (error) {
      console.error('[MarketSelectionContext] Error persisting state:', error);
    }
  }, [selectedCommodities, activeChart, timeWindow, systemFilter, categoryFilter, isInitialized]);

  // Selection methods
  const addCommodity = useCallback((commodity: SelectedCommodity) => {
    setSelectedCommodities(prev => {
      // Don't add if already selected or at max
      if (prev.some(c => c.id === commodity.id) || prev.length >= DEFAULT_MAX_SELECTIONS) {
        return prev;
      }
      return [...prev, commodity];
    });
    // Auto-open comparison panel when first item selected
    setIsComparisonPanelOpen(true);
  }, []);

  const removeCommodity = useCallback((id: string) => {
    setSelectedCommodities(prev => prev.filter(c => c.id !== id));
  }, []);

  const toggleCommodity = useCallback((commodity: SelectedCommodity) => {
    setSelectedCommodities(prev => {
      const isCurrentlySelected = prev.some(c => c.id === commodity.id);
      if (isCurrentlySelected) {
        return prev.filter(c => c.id !== commodity.id);
      }
      // Don't add if at max
      if (prev.length >= DEFAULT_MAX_SELECTIONS) {
        return prev;
      }
      return [...prev, commodity];
    });
    // Auto-open comparison panel when first item selected
    setIsComparisonPanelOpen(true);
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedCommodities([]);
    setIsComparisonPanelOpen(false);
  }, []);

  const isSelected = useCallback((id: string) => {
    return selectedCommodities.some(c => c.id === id);
  }, [selectedCommodities]);

  // Chart configuration methods
  const setActiveChart = useCallback((chart: ChartType) => {
    setActiveChartState(chart);
  }, []);

  const setTimeWindow = useCallback((window: TimeWindow) => {
    setTimeWindowState(window);
  }, []);

  // Filter methods
  const setSystemFilter = useCallback((system: string | null) => {
    setSystemFilterState(system);
  }, []);

  const setCategoryFilter = useCallback((category: string | null) => {
    setCategoryFilterState(category);
  }, []);

  // UI methods
  const toggleComparisonPanel = useCallback(() => {
    setIsComparisonPanelOpen(prev => !prev);
  }, []);

  // Computed values
  const selectionCount = selectedCommodities.length;
  const canAddMore = selectionCount < DEFAULT_MAX_SELECTIONS;
  const hasSelection = selectionCount > 0;

  // Phase 2: Register update listener
  const registerUpdateListener = useCallback((listener: MarketUpdateListener) => {
    listenersRef.current.add(listener);
    return () => {
      listenersRef.current.delete(listener);
    };
  }, []);

  // Phase 2: Force refresh trigger
  const forceRefresh = useCallback(() => {
    setRefreshTrigger(prev => prev + 1);
  }, []);

  // Phase 2: Notify all listeners of price update
  const notifyPriceUpdate = useCallback((event: PriceUpdateEvent) => {
    setLastPriceUpdate(event);
    listenersRef.current.forEach(listener => {
      if (listener.onPriceUpdate) {
        try {
          listener.onPriceUpdate(event);
        } catch (error) {
          console.error('[MarketSelectionContext] Listener error:', error);
        }
      }
    });
  }, []);

  // Phase 2: Supabase Realtime subscription
  useEffect(() => {
    // Only subscribe if we have selected commodities to track
    const commodityIds = selectedCommodities.map(c => c.id);

    // Clean up existing channel
    if (channelRef.current) {
      supabase.removeChannel(channelRef.current);
      channelRef.current = null;
      setIsRealtimeConnected(false);
    }

    // Create new channel for market updates
    const channel = supabase
      .channel('market-updates-' + Date.now())
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'merchant_commodities'
        },
        (payload) => {
          console.log('[MarketSelectionContext] merchant_commodities change:', payload.eventType);

          const newRecord = payload.new as Record<string, unknown>;
          if (newRecord && newRecord.commodity_id) {
            // Check if this update is for a selected commodity
            const commodityId = newRecord.commodity_id as string;
            // Only track if we have selected commodities AND this is one of them
            const isTracked = commodityIds.length > 0 && commodityIds.includes(commodityId);

            if (isTracked) {
              const priceEvent: PriceUpdateEvent = {
                commodityId,
                newPrice: Number(newRecord.last_buy_price || newRecord.last_sell_price || 0),
                activity: (newRecord.activity as 'buy' | 'sell') || 'buy',
                source: 'realtime',
                timestamp: new Date().toISOString()
              };
              notifyPriceUpdate(priceEvent);
            }
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'price_history'
        },
        (payload) => {
          console.log('[MarketSelectionContext] price_history insert:', payload);

          const newRecord = payload.new as Record<string, unknown>;
          if (newRecord && newRecord.commodity_id) {
            const commodityId = newRecord.commodity_id as string;
            // Only track if we have selected commodities AND this is one of them
            const isTracked = commodityIds.length > 0 && commodityIds.includes(commodityId);

            if (isTracked) {
              const rawPrice = newRecord.price;
              const parsedPrice = typeof rawPrice === 'number' ? rawPrice : Number(rawPrice);
              const priceEvent: PriceUpdateEvent = {
                commodityId,
                newPrice: !isNaN(parsedPrice) ? parsedPrice : 0,
                activity: newRecord.activity === 'sell' ? 'sell' : 'buy',
                source: (typeof newRecord.source === 'string' ? newRecord.source : 'unknown'),
                timestamp: (typeof newRecord.created_at === 'string' ? newRecord.created_at : new Date().toISOString())
              };
              notifyPriceUpdate(priceEvent);
            }
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'commodities_new'
        },
        (payload) => {
          console.log('[MarketSelectionContext] New commodity added:', payload);
          const newRecord = payload.new as Record<string, unknown>;
          if (newRecord && newRecord.id) {
            listenersRef.current.forEach(listener => {
              if (listener.onNewCommodity) {
                try {
                  listener.onNewCommodity(newRecord.id as string);
                } catch (error) {
                  console.error('[MarketSelectionContext] Listener error:', error);
                }
              }
            });
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'game_locations'
        },
        (payload) => {
          console.log('[MarketSelectionContext] New location added:', payload);
          const newRecord = payload.new as Record<string, unknown>;
          if (newRecord && newRecord.id) {
            listenersRef.current.forEach(listener => {
              if (listener.onNewLocation) {
                try {
                  listener.onNewLocation(newRecord.id as string);
                } catch (error) {
                  console.error('[MarketSelectionContext] Listener error:', error);
                }
              }
            });
          }
        }
      )
      .subscribe((status) => {
        console.log('[MarketSelectionContext] Realtime subscription status:', status);
        setIsRealtimeConnected(status === 'SUBSCRIBED');
      });

    channelRef.current = channel;

    // Cleanup on unmount or when selected commodities change
    return () => {
      if (channelRef.current) {
        console.log('[MarketSelectionContext] Cleaning up realtime channel');
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
        setIsRealtimeConnected(false);
      }
    };
  }, [selectedCommodities, notifyPriceUpdate]);

  // Memoized context value
  const contextValue = useMemo<MarketSelectionContextType>(() => ({
    // State
    selectedCommodities,
    activeChart,
    timeWindow,
    systemFilter,
    categoryFilter,
    isComparisonPanelOpen,
    maxSelections: DEFAULT_MAX_SELECTIONS,

    // Selection methods
    addCommodity,
    removeCommodity,
    toggleCommodity,
    clearSelection,
    isSelected,

    // Chart configuration methods
    setActiveChart,
    setTimeWindow,

    // Filter methods
    setSystemFilter,
    setCategoryFilter,

    // UI methods
    toggleComparisonPanel,
    setComparisonPanelOpen: setIsComparisonPanelOpen,

    // Computed values
    selectionCount,
    canAddMore,
    hasSelection,

    // Phase 2: Realtime updates
    lastPriceUpdate,
    isRealtimeConnected,
    registerUpdateListener,
    forceRefresh
  }), [
    selectedCommodities,
    activeChart,
    timeWindow,
    systemFilter,
    categoryFilter,
    isComparisonPanelOpen,
    addCommodity,
    removeCommodity,
    toggleCommodity,
    clearSelection,
    isSelected,
    setActiveChart,
    setTimeWindow,
    setSystemFilter,
    setCategoryFilter,
    toggleComparisonPanel,
    selectionCount,
    canAddMore,
    hasSelection,
    lastPriceUpdate,
    isRealtimeConnected,
    registerUpdateListener,
    forceRefresh
  ]);

  return (
    <MarketSelectionContext.Provider value={contextValue}>
      {children}
    </MarketSelectionContext.Provider>
  );
};

/**
 * Hook to access market selection context
 * @throws Error if used outside of MarketSelectionProvider
 */
export const useMarketSelection = (): MarketSelectionContextType => {
  const context = useContext(MarketSelectionContext);
  if (!context) {
    throw new Error('useMarketSelection must be used within a MarketSelectionProvider');
  }
  return context;
};

/**
 * Hook for optional market selection context (won't throw if not in provider)
 * Useful for components that may be used both inside and outside the provider
 */
export const useMarketSelectionOptional = (): MarketSelectionContextType | null => {
  return useContext(MarketSelectionContext);
};

export default MarketSelectionContext;
